import { FunctionComponent } from "react";
import {
    Typography,
    MenuItem,
    IconButton,
    SvgIcon,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    FormControl,
    FormLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    DialogActions,
} from "@mui/material/";
import { mdiClose, mdiPlusThick, mdiWrenchClock, mdiWeb, mdiPound } from "@mdi/js";
import { Icon } from "@mdi/react";
import { CustomSelect, Button, CustomInput, CustomSwitch } from "../../components";
import { CustomDatePicker } from "../../components/CustomDatePicker"
import { IProps } from "./IProps";
import { CustomTimePicker } from "../CustomTimePicker";
import { useScheduleDialog } from "./hook";
import { CustomSnackbar } from "../CustomSnackbar";
import { AlertDialog } from "../AlertDialog";

type DropdownItem = {
    value: string;
    description: string;
}

const TIME_INTERVAL: readonly string[] = ["Daily", "Weekly", "Monthly"];

const TIME_ZONES: readonly DropdownItem[] = [
    { value: "UTC-5:00", description: "Eastern Time (America/New_York" },
    { value: "UTC-6:00", description: "Central Time (America/Chicago" },
    { value: "UTC-7:00", description: "Mountain Time (America/Denver" },
    { value: "UTC-7:00", description: "Mountain Time - Arizona (Mountain/Phoenix)" },
    { value: "UTC-8:00", description: "Pacific Time (America/Los_Angeles)" },
    { value: "UTC-9:00", description: "Alaska Time (America/Chicago)" },
];

const DaysOfMonth: readonly number[] = [...Array<number>(31).keys()];

const ScheduleSwitch: FunctionComponent<{ value: boolean, onChange: () => void }> = ({ value, onChange }): JSX.Element => {
    return (
        <Box display="flex" alignItems="center" sx={{ marginTop: 3 }}>
            <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1BA0E9">
                    Do you want to schedule this report?
                </Typography>
                <Typography variant="body2" color="GrayText">
                    Please adjust the parameters below
                </Typography>
            </Box>
            <CustomSwitch value={value} onChange={(event) => onChange()} />
        </Box>
    )
}

export const ScheduleDialog: FunctionComponent<IProps> = (props) => {
    const {
        state,
        emails,
        snackbarState,
        openWarningModal,
        isScheduled,
        onCloseSnackbar,
        onCloseWarningModal,
        addNewEmailAdress,
        onChangeSwitch,
        onChange,
        onChangeRadio,
        onChangeDay,
        onChangeSelect,
        onSchedule
    } = useScheduleDialog(props.id!);

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="md"
                open={props.open}
            >
                <DialogTitle display="flex" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ color: "#1BA0E9", fontSize: 24, fontWeight: 600 }}>Schedule Report</Typography>
                    <IconButton onClick={props.onClose} >
                        <SvgIcon>
                            <Icon path={mdiClose} />
                        </SvgIcon>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                        <Box display="flex" alignItems="flex-start" gap={1}>
                            <Stack direction="column" spacing={1}>
                                {
                                    emails.map((value, index) => {
                                        return (
                                            <CustomInput disabled={!isScheduled} label={{ text: "email" }} key={index} width={300} height={35} value={value} onChange={(event) => onChange(event, index)} placeholder="johndoe@email.com" />
                                        )
                                    })
                                }
                            </Stack>
                            <Button
                                disabled={!isScheduled}
                                sx={{ marginTop: 3.8 }}
                                width={180}
                                height={35}
                                variant="outlined"
                                startIcon={<Icon path={mdiPlusThick} color="#1BA0E9" size="18px" />}
                                label="Add another email"
                                onClick={addNewEmailAdress}
                            />

                        </Box>
                        <ScheduleSwitch value={isScheduled} onChange={onChangeSwitch} />
                    </Box>
                    <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" my={4}>
                        <CustomSelect
                            label="Time Interval"
                            value={state.timeInterval}
                            name="timeInterval"
                            onChange={onChangeSelect}
                            disabled={!isScheduled}
                            StartIcon={(
                                <SvgIcon>
                                    <Icon path={mdiWrenchClock} />
                                </SvgIcon>
                            )}
                        >
                            {
                                TIME_INTERVAL.map((timeInterval, index) => {
                                    return (
                                        <MenuItem key={index} value={timeInterval} >
                                            {timeInterval}
                                        </MenuItem>
                                    )
                                })
                            }
                        </CustomSelect>
                        <CustomSelect
                            label="Time Zone"
                            value={state.timeZone}
                            name="timeZone"
                            onChange={onChangeSelect}
                            disabled={!isScheduled}
                            StartIcon={(
                                <SvgIcon>
                                    <Icon path={mdiWeb} />
                                </SvgIcon>
                            )}
                        >
                            {
                                TIME_ZONES.map((timeZone, index) => {
                                    return (
                                        <MenuItem key={index} value={timeZone.value} >
                                            {timeZone.description}
                                        </MenuItem>
                                    )
                                })
                            }
                        </CustomSelect>
                    </Box>
                    <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" my={4} >
                        <CustomSelect
                            label="Every"
                            value={state.every}
                            name="every"
                            onChange={onChangeSelect}
                            disabled={!isScheduled}
                            StartIcon={(
                                <SvgIcon>
                                    <Icon path={mdiPound} />
                                </SvgIcon>
                            )}
                        >
                            {
                                DaysOfMonth.map((day, index) => {
                                    return (
                                        <MenuItem value={day + 1} key={index} >
                                            {day + 1}
                                        </MenuItem>
                                    )
                                })
                            }
                        </CustomSelect>
                        <Box width="44%" >
                            <CustomTimePicker isDisabled={!isScheduled} label="At" width="100%" value={state.at} onChange={onChangeDay} />
                        </Box>
                    </Box>
                    <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" my={4} >
                        <Box width="44%" >
                            <CustomDatePicker disabled={!isScheduled} width={"100%"} label={{ text: "Starting On" }} onChange={() => null} />
                        </Box>
                        <Box width="44%" >
                            <CustomDatePicker disabled={!isScheduled} width={"100%"} label={{ text: "Starting On" }} onChange={() => null} />
                        </Box>
                    </Box>
                    <FormControl disabled={!isScheduled} >
                        <FormLabel>Attach this report as:</FormLabel>
                        <RadioGroup value={state.fileFormat} onChange={onChangeRadio} row>
                            <FormControlLabel value="PDF" control={<Radio />} label="PDF" />
                            <FormControlLabel value="CSV" control={<Radio />} label="CSV" />
                            <FormControlLabel value="XLSX" control={<Radio />} label="XLSX" />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" label="Cancel" height={40} onClick={props.onClose} />
                    <Button label="Save" height={40} onClick={() => onSchedule(
                        props.id,
                        props.reportType,
                        props.folderId,
                        props.reportTitle,
                        props.description,
                        props.columnIds,
                        props.filterIds,
                        props.filterDetails,
                        props.groupBy,
                        props.subGroupBy
                    )} />
                </DialogActions>
            </Dialog>
            <CustomSnackbar
                onClose={onCloseSnackbar}
                level={snackbarState.type as any}
                variant="secondary"
                message={snackbarState.message}
                open={snackbarState.isOpen}
            />
            <AlertDialog
                open={openWarningModal}
                type="warning"
                title="Schedule should not be empty"
                description="You can't open a schedule with empty data. Please fill in the form or set the schedule to off."
                onContinue={onCloseWarningModal}
            />
        </>
    )
}